import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid,
} from '../../../components/mui/Core/index'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import CreditInvoiceTable from './components/CreditInvoiceTable/credit-invoice-table'
import { downloadInvoice } from '../../../api/invoice/download-invoice'
import { openFileInBrowser } from '../../../utils/file-utils'
import { fileTypes } from '../../../constants/file-types'
import { businessMessages } from '../../../constants/business-message-constants'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import TableSearch from '../../../components/TableSearch/table-search'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import ClientSelection from '../../../components/ClientSelection/client-selection'
import SelectClientDropdown from '../../../components/dropdowns/SelectClientDropdown/select-client-dropdown'
import { userPermissionConstants } from '../../../constants/user-permission-constants'
import { hasUserPermission } from '../../../utils/user-utils'
import { fetchCreditInvoices } from './credit-invoice-slice'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'

function CreditInvoices() {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const creditInvoices = useSelector((state) => state.creditInvoice.creditInvoices)
  const [selectedClient, setSelectedClient] = useState(
    JSON.parse(sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT)),
  )
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })
  const searchByOptions = [
    {
      id: 'creditInvoiceNumber',
      text: Constants.creditInvoice.CREDIT_INVOICE_NUMBER,
      type: 'string',
    },
    {
      id: 'invoiceDateIssued',
      text: Constants.creditInvoice.DATE_ISSUED,
      type: 'date',
    },
    {
      id: 'appliedToInvoice',
      text: Constants.creditInvoice.APPLIED_TO,
      type: 'string',
    },
    {
      id: 'invoiceType',
      text: Constants.creditInvoice.INVOICE_TYPE,
      type: 'string',
    },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.creditInvoice.CREDIT_SUMMARY}`
    : Constants.creditInvoice.CREDIT_SUMMARY
  useBrowserTabTitles(browserTabTitle)

  useEffect(() => {
    /* Dispatch actions to fetch all invoices */
    async function getCreditInvoices() {
      setIsLoading(true)
      await Promise.all([dispatch(fetchCreditInvoices())])
    }
    if (!hasSwitchClientPermission || (hasSwitchClientPermission && selectedClient)) {
      getCreditInvoices()
        .then(() => setIsLoading(false))
        .catch((exception) => {
          setIsLoading(false)
          dispatch(
            setSnackbar({
              message: JSON.stringify(exception),
              severity: 'error',
              action: true,
            }),
          )
        })
    }
  }, [dispatch, hasSwitchClientPermission, selectedClient])

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const handleDisplayInvoice = async (invoiceId) => {
    const requestData = { invoiceId }
    await downloadInvoice(requestData)
      .then((response) => {
        if (response?.data) {
          openFileInBrowser(response.data, fileTypes.download.PDF).catch(() => {
            dispatch(
              setSnackbar({
                message: businessMessages.fileDownload.FAILED,
                severity: 'error',
                action: true,
              }),
            )
          })
        } else {
          dispatch(
            setSnackbar({
              message: businessMessages.fileDownload.FAILED,
              severity: 'error',
              action: true,
            }),
          )
        }
      })
      .catch(() => {
        dispatch(
          setSnackbar({
            message: businessMessages.fileDownload.Failed,
            severity: 'error',
            action: true,
          }),
        )
      })
  }

  const handleSelectClientDropdown = (client) => {
    setSelectedClient(client)
  }

  const tableSearchWrapperStyle = {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'end',
    paddingBottom: '4px',
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <GenericNav selected={Constants.genericNav.navLinks.INVOICES} user='authenticated' />
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container spacing={3}>
          <Grid size={{ xs: hasSwitchClientPermission ? 12 : 6 }}>
            <PageTitle title={Constants.creditInvoice.CREDIT_SUMMARY} />
          </Grid>
          {hasSwitchClientPermission &&
            (!selectedClient ? (
              <Grid size={{ xs: 6 }}>
                <ClientSelection
                  handleSelectOption={handleSelectClientDropdown}
                  message={Constants.invoices.CLIENT_SELECTION_MESSAGE}
                />
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 6 }}>
                  <SelectClientDropdown
                    currentState={selectedClient}
                    handleSelectOption={handleSelectClientDropdown}
                  />
                </Grid>
                {creditInvoices.length !== 0 && (
                  <Grid size={{ xs: 6 }} sx={tableSearchWrapperStyle}>
                    <TableSearch returnSearch={returnSearch} searchByOptions={searchByOptions} />
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <CreditInvoiceTable
                    filterBy={tableFilterBy}
                    filterValue={tableFilterValue}
                    handleDisplayInvoice={handleDisplayInvoice}
                    searchByOptions={searchByOptions}
                  />
                </Grid>
              </>
            ))}
          {!hasSwitchClientPermission && (
            <>
              <Grid size={{ xs: 6 }} sx={tableSearchWrapperStyle}>
                <TableSearch returnSearch={returnSearch} searchByOptions={searchByOptions} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CreditInvoiceTable
                  filterBy={tableFilterBy}
                  filterValue={tableFilterValue}
                  handleDisplayInvoice={handleDisplayInvoice}
                  searchByOptions={searchByOptions}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default CreditInvoices
